import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import { cylinderLabelStyle } from './utils';
import { ButtonNoBorder } from '../Common';

export const formFieldGridColumns = {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 6,
};

const FormFieldItem = ({ children, ...gridProps }) => (
    <Grid
        item
        {...formFieldGridColumns}
        {...gridProps}
    >
        {children}
    </Grid>
);

export const MoreFields = ({ children }) => <>{children}</>;
// Conditional `required={expr}` is already resolved at render time, so this naturally
// respects cases like `required={isCalifornia}` (only forced visible when actually required).
const containsRequiredField = (node) => {
    if (Array.isArray(node)) {
        return node.some(containsRequiredField);
    }
    if (!React.isValidElement(node)) {
        return false;
    }
    if (node.props?.required) {
        return true;
    }
    return containsRequiredField(node.props?.children);
};

export const CreateInventoryFormPanel = ({
    children,
    enabled = true,
    flattenStackedFields = false,
    initialVisibleFields = 10,
    title = 'New Inventory',
}) => {
    const [showHiddenFields, setShowHiddenFields] = useState(false);

    // Some forms (Refrigeration, ComfortCooling, ProcessCooling) wrap pairs of related fields
    // in a <Stack> inside a single FormFieldItem. flattenStackedFields pulls those out into
    // individual grid cells so they each get the standard two-column layout.
    const flattenChildren = (items) => (
        React.Children.toArray(items).flatMap((child) => {
            if (!React.isValidElement(child)) {
                return child;
            }

            if (child.type === React.Fragment) {
                return flattenChildren(child.props.children);
            }

            const childContent = child.props?.children;
            if (flattenStackedFields && React.isValidElement(childContent) && childContent.type === Stack) {
                return flattenChildren(childContent.props.children).map((stackChild, index) => (
                    <FormFieldItem key={`${child.key || 'field'}-${index}`}>
                        {stackChild}
                    </FormFieldItem>
                ));
            }

            return child;
        })
    );

    const childArray = React.Children.toArray(children);
    const usesMoreFieldsMarker = childArray.some(
        (child) => React.isValidElement(child) && child.type === MoreFields
    );

    let defaultFields;
    let extraFields;
    if (usesMoreFieldsMarker) {
        const defaultTop = [];
        const extraTop = [];
        childArray.forEach((child) => {
            if (React.isValidElement(child) && child.type === MoreFields) {
                extraTop.push(...React.Children.toArray(child.props.children));
            } else {
                defaultTop.push(child);
            }
        });
        defaultFields = flattenStackedFields ? flattenChildren(defaultTop) : defaultTop;
        extraFields = flattenStackedFields ? flattenChildren(extraTop) : extraTop;
    } else {
        const allFields = flattenStackedFields ? flattenChildren(children) : childArray;
        // Never collapse a currently-required field: extend the visible cutoff so it always
        // reaches the last required field. Truly-optional trailing fields still collapse.
        const lastRequiredIndex = allFields.reduce(
            (last, field, index) => (containsRequiredField(field) ? index : last),
            -1
        );
        const cutoff = Math.max(initialVisibleFields, lastRequiredIndex + 1);
        defaultFields = allFields.slice(0, cutoff);
        extraFields = allFields.slice(cutoff);
    }

    // Re-key after combining so the two buckets never collide on React keys.
    const visibleFields = (showHiddenFields ? [...defaultFields, ...extraFields] : defaultFields)
        .map((field, index) => (
            React.isValidElement(field) ? React.cloneElement(field, { key: `form-field-${index}` }) : field
        ));
    const hasHiddenFields = extraFields.length > 0;

    if (!enabled) {
        return (
            <Grid item xs={12}>
                <Accordion
                    variant="outlined"
                    expanded
                    sx={{ '&.MuiAccordion-root:before': { display: 'none' }, borderRadius: '4px' }}
                >
                    <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                        <Grid
                            container
                            columnSpacing={{ xs: 0, sm: 4 }}
                            rowSpacing={{ xs: 3, sm: 4 }}
                            alignItems="flex-start"
                        >
                            {visibleFields}
                            {hasHiddenFields && (
                                <Grid container item xs={12} justifyContent="center">
                                    <ButtonNoBorder
                                        buttonText={showHiddenFields ? 'Hide Extra Fields' : 'View More Fields'}
                                        onClick={() => setShowHiddenFields(!showHiddenFields)}
                                        sx={{
                                            minHeight: '42px',
                                            minWidth: { xs: '100%', sm: 180 },
                                            px: 2,
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>
        );
    }

    return (
        <Grid item xs={12}>
            <Accordion
                variant="outlined"
                expanded
            >
                <AccordionSummary>
                    <Typography sx={cylinderLabelStyle}>
                        {title}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                    <Grid
                        container
                        columnSpacing={{ xs: 0, sm: 4 }}
                        rowSpacing={{ xs: 3, sm: 4 }}
                        alignItems="flex-start"
                    >
                        {visibleFields}
                        {hasHiddenFields && (
                            <Grid container item xs={12} justifyContent="center">
                                <ButtonNoBorder
                                    buttonText={showHiddenFields ? 'Hide Extra Fields' : 'View More Fields'}
                                    onClick={() => setShowHiddenFields(!showHiddenFields)}
                                    sx={{
                                        minHeight: '42px',
                                        minWidth: { xs: '100%', sm: 180 },
                                        px: 2,
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

export default FormFieldItem;
